import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "../../lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const { state } = useLocation();
  const email = state?.email || "";

  // Redirect back if no email was passed
  useEffect(() => {
    if (!email) nav("/forgot-password", { replace: true });
  }, [email, nav]);

  // ── OTP state ────────────────────────────────────────────────────────────────
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;          // digits only
    const next = [...otp];
    next[idx] = val.slice(-1);               // single digit
    setOtp(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    pasted.split("").forEach((c, i) => { next[i] = c; });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    e.preventDefault();
  };

  // ── Password state ───────────────────────────────────────────────────────────
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);

  // ── Submission ───────────────────────────────────────────────────────────────
  const [err, setErr]         = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    const otpCode = otp.join("");
    if (otpCode.length < 6) return setErr("Please enter the full 6-digit OTP.");
    if (newPassword.length < 8) return setErr("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return setErr("Passwords do not match.");

    setLoading(true);
    try {
      await api.post("/admin/reset-password", {
        email,
        otp: otpCode,
        newPassword,
        confirmPassword,
      });
      setSuccess(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ───────────────────────────────────────────────────────────────
  const [resendTimer, setResendTimer] = useState(60);
  const [resending, setResending]     = useState(false);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const resendOtp = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    setErr("");
    try {
      await api.post("/admin/forgot-password", { email });
      setResendTimer(60);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResending(false);
    }
  };

  // ── Success screen ───────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-white grid place-items-center p-4">
        <div className="w-full max-w-md border rounded-2xl p-8 text-center space-y-4">
          <div className="text-5xl">✅</div>
          <h1 className="text-2xl font-bold">Password Reset!</h1>
          <p className="text-sm text-gray-600">
            Your password has been updated. A confirmation email has been sent.
            <br />Redirecting to login…
          </p>
        </div>
      </div>
    );
  }

  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <div className="min-h-screen bg-white grid place-items-center p-4">
      <div className="w-full max-w-md border rounded-2xl p-6">
        <Link
          to="/forgot-password"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-black mb-6 transition-colors"
        >
          ← Back
        </Link>

        <h1 className="text-2xl font-bold">Reset Password</h1>
        <p className="text-sm text-gray-600 mt-1">
          Enter the OTP sent to{" "}
          <span className="font-semibold text-black">{email}</span>{" "}
          and choose a new password.
        </p>

        <form onSubmit={onSubmit} className="space-y-5 mt-6">

          {/* ── Email (read-only) ── */}
          <div>
            <label className="text-sm font-semibold">Email</label>
            <input
              readOnly
              value={email}
              className="mt-2 w-full border rounded-xl px-3 py-2 bg-gray-50 text-gray-500 outline-none cursor-default select-none"
            />
          </div>

          {/* ── OTP boxes ── */}
          <div>
            <label className="text-sm font-semibold">One-Time Password</label>
            <div
              className="flex justify-between gap-1.5 sm:gap-2 mt-3"
              onPaste={handlePaste}
            >
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  className={`
                    w-9 h-11 sm:w-12 sm:h-[52px] shrink-0
                    text-center text-xl font-bold border rounded-xl
                    outline-none transition-all
                    focus:ring-2 focus:ring-black focus:border-black
                    ${digit ? "bg-black text-white border-black" : "bg-white text-black border-gray-300"}
                  `}
                />
              ))}
            </div>

            {/* Resend */}
            <p className="text-xs text-gray-500 mt-2 text-right">
              Didn't get it?{" "}
              <button
                type="button"
                onClick={resendOtp}
                disabled={resendTimer > 0 || resending}
                className="font-semibold text-black disabled:text-gray-400"
              >
                {resending ? "Resending…" : resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
              </button>
            </p>
          </div>

          {/* ── New Password ── */}
          <div>
            <label className="text-sm font-semibold">New Password</label>
            <div className="relative mt-2">
              <input
                type={showNew ? "text" : "password"}
                required
                placeholder="Min. 8 characters"
                className="w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-black"
              >
                {showNew ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          {/* ── Confirm Password ── */}
          <div>
            <label className="text-sm font-semibold">Confirm Password</label>
            <div className="relative mt-2">
              <input
                type={showConfirm ? "text" : "password"}
                required
                placeholder="Re-enter your password"
                className={`
                  w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2
                  ${passwordsMismatch ? "border-red-400 focus:ring-red-400" : "focus:ring-black"}
                  ${passwordsMatch ? "border-green-400 focus:ring-green-400" : ""}
                `}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-black"
              >
                {showConfirm ? "🙈" : "👁️"}
              </button>
            </div>
            {passwordsMatch && (
              <p className="text-xs text-green-600 font-medium mt-1">✓ Passwords match</p>
            )}
            {passwordsMismatch && (
              <p className="text-xs text-red-500 font-medium mt-1">✗ Passwords do not match</p>
            )}
          </div>

          {err && <div className="text-sm text-red-600">{err}</div>}

          <button
            disabled={loading}
            className="w-full bg-black text-white rounded-xl py-2 font-semibold hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Resetting…" : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}